import { useEffect, useRef } from "react";
import Hls from "hls.js";
import { auth } from "config/firebase/firebase";

interface HlsVideoPlayerProps {
  src: string;
  style?: React.CSSProperties;
}

/**
 * Creates a custom hls.js loader that routes all EEN segment and manifest requests
 * through the POST /een/video-proxy endpoint, authenticating via Firebase ID token.
 */
function createProxyLoader(apiUrl: string) {
  return class ProxyLoader {
    stats = {
      aborted: false,
      loaded: 0,
      retry: 0,
      total: 0,
      chunkCount: 0,
      bwEstimate: 0,
      loading: { start: 0, first: 0, end: 0 },
      parsing: { start: 0, end: 0 },
      buffering: { start: 0, first: 0, end: 0 },
    };
    context: any = null;
    private controller = new AbortController();

    constructor(_: any) {}

    load(context: any, _config: any, callbacks: any): void {
      this.context = context;
      this.stats.loading.start = performance.now();

      // Get a fresh Firebase ID token for each request so it never expires mid-stream
      const user = auth.currentUser;
      const tokenPromise = user ? user.getIdToken() : Promise.reject(new Error("No Firebase user signed in"));

      tokenPromise
        .then((idToken) =>
          fetch(`${apiUrl}/een/video-proxy`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${idToken}`,
            },
            body: JSON.stringify({ url: context.url }),
            signal: this.controller.signal,
          })
        )
        .then(async (res) => {
          if (!res.ok) throw new Error(`Proxy error: ${res.status}`);
          this.stats.loading.first = performance.now();

          const data: string | ArrayBuffer =
            context.responseType === "arraybuffer"
              ? await res.arrayBuffer()
              : await res.text();

          this.stats.loading.end = performance.now();
          this.stats.loaded = this.stats.total =
            typeof data === "string" ? data.length : data.byteLength;

          callbacks.onSuccess(
            { data, url: context.url, code: res.status },
            this.stats,
            context,
            res
          );
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") return;
          callbacks.onError(
            { code: 0, text: err.message },
            context,
            null,
            this.stats
          );
        });
    }

    abort(): void {
      this.controller.abort();
    }

    destroy(): void {
      this.abort();
    }
  };
}

const HlsVideoPlayer: React.FC<HlsVideoPlayerProps> = ({ src, style }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const apiUrl = process.env.REACT_APP_API_URL ?? "";

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (Hls.isSupported()) {
      const ProxyLoader = createProxyLoader(apiUrl);
      const hls = new Hls({ loader: ProxyLoader as any });
      hls.loadSource(src);
      hls.attachMedia(video);
      return () => hls.destroy();
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS — direct playback (no custom loader support)
      video.src = src;
    }
  }, [src, apiUrl]);

  return (
    <video
      ref={videoRef}
      controls
      style={{ width: "100%", maxWidth: 640, display: "block", ...style }}
    />
  );
};

export default HlsVideoPlayer;
